import { ComposerSession } from '../ComposerSession';
import { ChordQuality } from '../../../types';
import { CadenceType } from '../StructureEngine';

/**
 * ResolutionManager
 * P0 FIX: Enhanced "Grammy-Winning" resolution logic for song endings.
 * 
 * Key improvements:
 * - Extended lookahead: 12 touches (not 5)
 * - Pre-cadential preparation starting at 85% progress
 * - Genre-aware resolution sequences with proper V→I preparation
 * - Ritardando signaling for natural slowdown
 */
export class ResolutionManager {

    // P0 FIX: Extended lookahead from 5 to 12 touches
    public static readonly RESOLUTION_LOOKAHEAD = 12;

    // P0 FIX: Track ritardando state
    private ritardandoActive: boolean = false;

    /**
     * P0 FIX: Check if resolution mode should be active
     * Now triggers at 12 touches remaining (not 5)
     */
    public shouldActivateResolution(touchesRemaining: number): boolean {
        return touchesRemaining <= ResolutionManager.RESOLUTION_LOOKAHEAD;
    }

    /**
     * P0 FIX: Get pre-cadential preparation chord
     * Returns the appropriate preparation chord based on song position
     * - 85-90%: Subdominant preparation (ii or IV)
     * - 90-95%: Dominant preparation (V or V7)
     * - 95%+: Resolution (I)
     */
    public getPreCadentialChord(
        songProgress: number,
        scaleNotes: number[],
        progressionComplexity: 'Simple' | 'Medium' | 'Complex' | 'Exotic'
    ): { degree: number; quality: ChordQuality } | null {

        // Not yet in pre-cadential zone
        if (songProgress < 0.85) return null;

        // Different progressions have different pre-cadential approaches
        if (songProgress < 0.90) {
            // SUBDOMINANT ZONE: ii or IV
            if (progressionComplexity === 'Complex' || progressionComplexity === 'Exotic') {
                return { degree: 1, quality: 'minor7' }; // ii7
            }
            return { degree: 3, quality: 'major' }; // IV
        }

        if (songProgress < 0.95) {
            // DOMINANT ZONE: V7
            return { degree: 4, quality: 'dominant7' }; // V7
        }

        // TONIC ZONE: I
        return { degree: 0, quality: 'major' }; // I
    }

    /**
     * P0 FIX: Check if ritardando should be active
     */
    public isRitardandoActive(): boolean {
        return this.ritardandoActive;
    }

    /**
     * P0 FIX: Aggressively rewrites the chord forecast when approaching the end of the song.
     * Now with 12-touch sequences and proper dominant preparation.
     */
    public updateResolutionForecast(session: ComposerSession, touchesRemaining: number, scaleNotes: number[]) {
        // Clear forecast to force resolution
        session.chordForecast = [];

        // P0 FIX: Activate ritardando in final 8 touches
        this.ritardandoActive = touchesRemaining <= 8;

        // Determine Strategy based on Vibe
        const vibe = session.vibeName.toLowerCase();
        const progressionName = session.progression.name;

        let strategy: 'Pop' | 'Jazz' | 'Epic' | 'Gospel' = 'Pop';

        if (progressionName.includes('Jazz') || vibe.includes('city') || vibe.includes('lounge') || vibe.includes('coffee')) {
            strategy = 'Jazz';
        } else if (progressionName.includes('Epic') || progressionName.includes('Space') || vibe.includes('battle') || vibe.includes('dragon')) {
            strategy = 'Epic';
        } else if (progressionName.includes('Gospel') || progressionName.includes('Soul') || vibe.includes('church') || vibe.includes('joy')) {
            strategy = 'Gospel';
        }

        // P0 FIX: Generate EXTENDED Final Sequence (12 chords instead of 5)
        // Reverse Order: position 12 (Start of sequence) → position 1 (Final chord)
        const sequence = this.buildResolutionSequence(strategy);

        // Fill Forecast from the sequence based on touchesRemaining
        const index = Math.min(touchesRemaining - 1, sequence.length - 1);

        if (index >= 0) {
            for (let i = index; i >= 0; i--) {
                const step = sequence[i];
                this.pushExplicitForecastItem(session, step, scaleNotes);
            }
        }
    }

    /**
     * P0 FIX: Build extended 12-chord resolution sequences
     * Each sequence now includes proper preparation (ii→V→I or IV→V→I)
     */
    private buildResolutionSequence(strategy: 'Pop' | 'Jazz' | 'Epic' | 'Gospel'): Array<{
        degree: number;
        quality: string;
        isBorrowed?: boolean;
        rootOffset?: number
    }> {
        const sequence: Array<{ degree: number; quality: string; isBorrowed?: boolean; rootOffset?: number }> = [];

        if (strategy === 'Jazz') {
            // Jazz Cat: Extended ii-V-I with substitutions
            // 12→1: I | ii7 | V7 | Imaj7 | vi7 | ii7 | V7 | iii7 | vi7 | ii7 | bII7 | Imaj7
            sequence.push({ degree: 0, quality: 'major7' });     // 1. Final Imaj7
            sequence.push({ degree: 1, quality: 'dominant7', rootOffset: -1 }); // 2. bII7 (Tritone sub)
            sequence.push({ degree: 1, quality: 'minor7' });     // 3. ii7
            sequence.push({ degree: 5, quality: 'minor7' });     // 4. vi7
            sequence.push({ degree: 2, quality: 'minor7' });     // 5. iii7
            sequence.push({ degree: 4, quality: 'dominant7' });  // 6. V7
            sequence.push({ degree: 1, quality: 'minor7' });     // 7. ii7
            sequence.push({ degree: 5, quality: 'minor7' });     // 8. vi7
            sequence.push({ degree: 0, quality: 'major7' });     // 9. Imaj7
            sequence.push({ degree: 4, quality: 'dominant7' });  // 10. V7
            sequence.push({ degree: 1, quality: 'minor7' });     // 11. ii7
            sequence.push({ degree: 0, quality: 'major7' });     // 12. Start I

        } else if (strategy === 'Epic') {
            // Epic Hero: Extended bVI-bVII-I with dramatic builds
            // 12→1: i | bVI | bVII | i | bVI | bVII | iv | bVI | bVII | IV | V | I (Picardy)
            sequence.push({ degree: 0, quality: 'major' });      // 1. I (Picardy Third!)
            sequence.push({ degree: 4, quality: 'major' });      // 2. V
            sequence.push({ degree: 3, quality: 'major' });      // 3. IV
            sequence.push({ degree: 6, quality: 'major', isBorrowed: true }); // 4. bVII
            sequence.push({ degree: 5, quality: 'major', isBorrowed: true }); // 5. bVI
            sequence.push({ degree: 3, quality: 'minor' });      // 6. iv
            sequence.push({ degree: 6, quality: 'major', isBorrowed: true }); // 7. bVII
            sequence.push({ degree: 5, quality: 'major', isBorrowed: true }); // 8. bVI
            sequence.push({ degree: 0, quality: 'minor' });      // 9. i
            sequence.push({ degree: 6, quality: 'major', isBorrowed: true }); // 10. bVII
            sequence.push({ degree: 5, quality: 'major', isBorrowed: true }); // 11. bVI
            sequence.push({ degree: 0, quality: 'minor' });      // 12. Start i

        } else if (strategy === 'Gospel') {
            // Gospel Glory: Extended plagal with 7ths and suspensions
            // 12→1: I | IV | V7sus | I | IV | I7 | IV | ii7 | V7 | IV | Vsus | I
            sequence.push({ degree: 0, quality: 'major' });      // 1. Final I
            sequence.push({ degree: 4, quality: 'sus4' });       // 2. Vsus4 (tension before resolution)
            sequence.push({ degree: 3, quality: 'major' });      // 3. IV (Plagal)
            sequence.push({ degree: 4, quality: 'dominant7' });  // 4. V7
            sequence.push({ degree: 1, quality: 'minor7' });     // 5. ii7
            sequence.push({ degree: 3, quality: 'major' });      // 6. IV
            sequence.push({ degree: 0, quality: 'dominant7' });  // 7. I7 (Secondary of IV)
            sequence.push({ degree: 3, quality: 'major' });      // 8. IV
            sequence.push({ degree: 0, quality: 'major' });      // 9. I
            sequence.push({ degree: 4, quality: 'sus4' });       // 10. Vsus4
            sequence.push({ degree: 3, quality: 'major' });      // 11. IV
            sequence.push({ degree: 0, quality: 'major' });      // 12. Start I

        } else {
            // Pop Anthem: Extended IV-V-I with emotional vi and ii
            // 12→1: I | IV | V | I | vi | IV | I | vi | ii | IV | V | I
            sequence.push({ degree: 0, quality: 'major' });      // 1. Final I
            sequence.push({ degree: 4, quality: 'major' });      // 2. V (Dominant)
            sequence.push({ degree: 3, quality: 'major' });      // 3. IV (Subdominant)
            sequence.push({ degree: 1, quality: 'minor' });      // 4. ii (Pre-dominant)
            sequence.push({ degree: 5, quality: 'minor' });      // 5. vi
            sequence.push({ degree: 0, quality: 'major' });      // 6. I
            sequence.push({ degree: 3, quality: 'major' });      // 7. IV
            sequence.push({ degree: 5, quality: 'minor' });      // 8. vi
            sequence.push({ degree: 0, quality: 'major' });      // 9. I
            sequence.push({ degree: 4, quality: 'major' });      // 10. V
            sequence.push({ degree: 3, quality: 'major' });      // 11. IV
            sequence.push({ degree: 0, quality: 'major' });      // 12. Start I
        }

        return sequence;
    }

    /**
     * Explicitly push a chord with potential chromatic alterations (borrowed/tritone)
     */
    private pushExplicitForecastItem(
        session: ComposerSession,
        step: { degree: number; quality: string; isBorrowed?: boolean; rootOffset?: number },
        scaleNotes: number[]
    ) {
        // Calculate Root
        let noteIndex = scaleNotes[step.degree % scaleNotes.length];

        // Apply Offset
        let rootMidi = noteIndex;
        if (step.rootOffset) {
            rootMidi += step.rootOffset;
        }

        // HACK: For 'Epic' borrowed chords (bVI, bVII) defined in strategy
        if (step.isBorrowed && !step.rootOffset) {
            rootMidi -= 1;
        }

        // Generate Notes based on Quality
        const chordNotes = this.buildResolutionChordNotes(rootMidi, step.quality);

        session.chordForecast.push({
            degree: step.degree,
            quality: step.quality as ChordQuality,
            notes: chordNotes,
            rootMidi: rootMidi,
            isSecondaryDominant: false
        });
    }

    private buildResolutionChordNotes(root: number, quality: string): number[] {
        // Simple Triad/7th builder
        const notes = [root];

        // Handle sus chords
        if (quality === 'sus2') {
            notes.push(root + 2); // 2nd
            notes.push(root + 7); // 5th
            return notes;
        }
        if (quality === 'sus4') {
            notes.push(root + 5); // 4th
            notes.push(root + 7); // 5th
            return notes;
        }

        // 3rd
        if (quality === 'minor' || quality === 'diminished' || quality.includes('minor')) {
            notes.push(root + 3);
        } else {
            notes.push(root + 4); // Major/Dom
        }

        // 5th
        if (quality === 'diminished') {
            notes.push(root + 6);
        } else {
            notes.push(root + 7);
        }

        // 7th
        if (quality.includes('7')) {
            if (quality === 'major7') {
                notes.push(root + 11);
            } else {
                notes.push(root + 10); // minor7, dominant7
            }
        }

        return notes;
    }

    /**
     * P0 FIX: Reset ritardando state for new session
     */
    public reset(): void {
        this.ritardandoActive = false;
    }
}
